/**
 * Vuex module for PDF Document Editor
 * Manages annotations, selected annotation, and field values with proper Vue reactivity
 */

import Vue from "vue";
import {
  GetDocumentTemplate,
  GetDocumentTemplateFile,
} from "../../DriveScout/API/models/DocumentModel";

function normalizeSignatureAnnotation(annotation) {
  if (!annotation || annotation.type !== "signature") {
    return annotation;
  }

  const properties = { ...(annotation.properties || {}) };
  const assignableTo = Array.isArray(annotation.assignableTo)
    ? annotation.assignableTo.filter(Boolean)
    : [];

  const signeeType =
    annotation.signee_type ||
    properties.signee_type ||
    assignableTo[0] ||
    "student";

  return {
    ...annotation,
    signee_type: signeeType,
    assignableTo: signeeType ? [signeeType] : [],
    properties: {
      ...properties,
      // signee_type is only at top-level now, not duplicated in properties
    },
  };
}

const state = {
  // Annotations stored as an object (Map-like) for easy access and reactivity
  annotations: {},
  // ID generator state
  nextAnnotationId: 1,
  // Currently selected annotation
  selectedAnnotation: null,
  // Field values for document completion mode
  fieldValues: {},
  // UI state
  addingField: false,
  addingFieldType: "",
  editingField: "",
  // PDF related state
  pdfData: null,
  pdfUrl: null,
  templateData: null,
  loadingPdf: false,
  pdfError: null,
};

const mutations = {
  /**
   * Add a new annotation
   */
  ADD_ANNOTATION(state, annotation) {
    const normalized = normalizeSignatureAnnotation(annotation);
    Vue.set(state.annotations, normalized.id, normalized);
  },

  /**
   * Update an existing annotation
   */
  UPDATE_ANNOTATION(state, { id, updates }) {
    const annotation = state.annotations[id];
    if (annotation) {
      // Merge updates with existing annotation
      const merged = normalizeSignatureAnnotation({
        ...annotation,
        ...updates,
      });
      Vue.set(state.annotations, id, merged);
    }
  },

  /**
   * Remove an annotation
   */
  REMOVE_ANNOTATION(state, id) {
    Vue.delete(state.annotations, id);

    // Clear selection if deleted annotation was selected
    if (state.selectedAnnotation && state.selectedAnnotation.id === id) {
      state.selectedAnnotation = null;
    }
  },

  /**
   * Update annotation position
   */
  UPDATE_ANNOTATION_POSITION(state, { id, position }) {
    const annotation = state.annotations[id];
    if (annotation) {
      Vue.set(state.annotations, id, {
        ...annotation,
        position: { ...annotation.position, ...position },
      });
    }
  },

  /**
   * Set selected annotation
   */
  SET_SELECTED_ANNOTATION(state, annotation) {
    state.selectedAnnotation = annotation;
  },

  /**
   * Clear all annotations
   */
  CLEAR_ANNOTATIONS(state) {
    state.annotations = {};
    state.nextAnnotationId = 1;
    state.selectedAnnotation = null;
  },

  /**
   * Import annotations (used when loading a template)
   */
  IMPORT_ANNOTATIONS(state, annotations) {
    state.annotations = {};
    state.nextAnnotationId = 1;

    annotations.forEach((annotation) => {
      const normalized = normalizeSignatureAnnotation(annotation);
      Vue.set(state.annotations, normalized.id, normalized);

      // Update nextId to avoid conflicts
      const idNum = parseInt(normalized.id.replace("ann_", ""));
      if (!isNaN(idNum) && idNum >= state.nextAnnotationId) {
        state.nextAnnotationId = idNum + 1;
      }
    });
  },

  /**
   * Generate and return next annotation ID
   */
  INCREMENT_ANNOTATION_ID(state) {
    state.nextAnnotationId++;
  },

  /**
   * Set field value
   */
  SET_FIELD_VALUE(state, { fieldName, value }) {
    Vue.set(state.fieldValues, fieldName, value);
  },

  /**
   * Clear all field values
   */
  CLEAR_FIELD_VALUES(state) {
    state.fieldValues = {};
  },

  /**
   * Set adding field state
   */
  SET_ADDING_FIELD(state, { adding, fieldType }) {
    state.addingField = adding;
    state.addingFieldType = fieldType;
  },

  /**
   * Set editing field state
   */
  SET_EDITING_FIELD(state, fieldName) {
    state.editingField = fieldName;
  },

  /**
   * PDF related mutations
   */
  SET_PDF_DATA(state, pdfData) {
    state.pdfData = pdfData;
  },

  SET_PDF_URL(state, pdfUrl) {
    state.pdfUrl = pdfUrl;
  },

  SET_TEMPLATE_DATA(state, templateData) {
    state.templateData = templateData;
  },

  SET_PDF_LOADING(state, loading) {
    state.loadingPdf = loading;
  },

  SET_PDF_ERROR(state, error) {
    state.pdfError = error;
  },
};

const actions = {
  /**
   * Add a new annotation
   */
  addAnnotation({ commit, state }, annotation) {
    return new Promise((resolve) => {
      const newAnnotation = { ...annotation };

      if (annotation.position) {
        newAnnotation.position = { ...annotation.position };
      }

      if (annotation.properties) {
        newAnnotation.properties = { ...annotation.properties };
      }

      if (Array.isArray(annotation.assignableTo)) {
        newAnnotation.assignableTo = [...annotation.assignableTo];
      }

      if (!newAnnotation.id) {
        newAnnotation.id = `ann_${state.nextAnnotationId}`;
        commit("INCREMENT_ANNOTATION_ID");
      }

      const normalized = normalizeSignatureAnnotation(newAnnotation);

      commit("ADD_ANNOTATION", normalized);
      console.log(
        "[Vuex] Added annotation:",
        normalized.id,
        "Total:",
        Object.keys(state.annotations).length,
      );
      resolve(normalized);
    });
  },

  /**
   * Update an existing annotation
   */
  updateAnnotation({ commit, getters }, { id, updates }) {
    commit("UPDATE_ANNOTATION", { id, updates });

    // Update selected annotation if it's the one being updated
    const annotation = getters.getAnnotationById(id);
    if (annotation && getters.selectedAnnotation?.id === id) {
      commit("SET_SELECTED_ANNOTATION", annotation);
    }
  },

  /**
   * Remove an annotation
   */
  removeAnnotation({ commit }, id) {
    commit("REMOVE_ANNOTATION", id);
  },

  /**
   * Move an annotation to a new position
   */
  moveAnnotation({ commit, getters }, { id, position }) {
    commit("UPDATE_ANNOTATION_POSITION", { id, position });

    // Update selected annotation if it's the one being moved
    const annotation = getters.getAnnotationById(id);
    if (annotation && getters.selectedAnnotation?.id === id) {
      commit("SET_SELECTED_ANNOTATION", annotation);
    }
  },

  /**
   * Duplicate an annotation
   */
  duplicateAnnotation({ commit, state, getters }, id) {
    const original = getters.getAnnotationById(id);
    if (!original) {
      return null;
    }

    const duplicateId = `ann_${state.nextAnnotationId}`;
    commit("INCREMENT_ANNOTATION_ID");

    const duplicate = {
      ...original,
      id: duplicateId,
      fieldName: `${original.fieldName}_copy`,
      label: `${original.label} (Copy)`,
      position: {
        ...original.position,
        x: original.position.x + 20,
        y: original.position.y + 20,
      },
    };

    commit("ADD_ANNOTATION", duplicate);
    return duplicate;
  },

  /**
   * Select an annotation
   */
  selectAnnotation({ commit }, annotation) {
    console.log("[Vuex] selectAnnotation called with:", annotation);
    commit("SET_SELECTED_ANNOTATION", annotation);
    console.log("[Vuex] selectedAnnotation set in state");
  },

  /**
   * Clear selection
   */
  clearSelection({ commit }) {
    commit("SET_SELECTED_ANNOTATION", null);
  },

  /**
   * Clear all annotations
   */
  clearAnnotations({ commit }) {
    commit("CLEAR_ANNOTATIONS");
  },

  /**
   * Import annotations from template
   */
  importAnnotations({ commit }, annotations) {
    commit("IMPORT_ANNOTATIONS", annotations);
  },

  /**
   * Export all annotations
   */
  exportAnnotations({ getters }) {
    return getters.allAnnotations;
  },

  /**
   * Set field value
   */
  setFieldValue({ commit }, { fieldName, value }) {
    commit("SET_FIELD_VALUE", { fieldName, value });
  },

  /**
   * Start adding a field
   */
  startAddingField({ commit }, fieldType) {
    commit("SET_ADDING_FIELD", { adding: true, fieldType });
    commit("SET_SELECTED_ANNOTATION", null);
  },

  /**
   * Cancel adding a field
   */
  cancelAddingField({ commit }) {
    commit("SET_ADDING_FIELD", { adding: false, fieldType: "" });
  },

  /**
   * Start editing a field
   */
  startEditingField({ commit }, fieldName) {
    commit("SET_EDITING_FIELD", fieldName);
  },

  /**
   * Stop editing a field
   */
  stopEditingField({ commit }) {
    commit("SET_EDITING_FIELD", "");
  },

  /**
   * Reset entire editor state
   */
  resetEditor({ commit }) {
    commit("CLEAR_ANNOTATIONS");
    commit("CLEAR_FIELD_VALUES");
    commit("SET_SELECTED_ANNOTATION", null);
    commit("SET_ADDING_FIELD", { adding: false, fieldType: "" });
    commit("SET_EDITING_FIELD", "");
  },

  /**
   * Load document template with PDF
   */
  async loadDocumentTemplate({ commit }, templateId) {
    commit("SET_PDF_LOADING", true);
    commit("SET_PDF_ERROR", null);

    try {
      // Fetch template data
      const templateResponse = await GetDocumentTemplate(templateId);
      const template = templateResponse.data.template;
      commit("SET_TEMPLATE_DATA", template);

      // Set the PDF URL (this should be the proxy URL from the backend)
      if (template.file_url) {
        commit("SET_PDF_URL", template.file_url);
      }

      return template;
    } catch (error) {
      console.error("Failed to load document template:", error);
      commit("SET_PDF_ERROR", error.message || "Failed to load template");
      throw error;
    } finally {
      commit("SET_PDF_LOADING", false);
    }
  },

  /**
   * Load PDF file data for PDF.js
   */
  async loadTemplatePdfData({ commit }, templateId) {
    commit("SET_PDF_LOADING", true);
    commit("SET_PDF_ERROR", null);

    try {
      // Fetch PDF file as ArrayBuffer
      const response = await GetDocumentTemplateFile(templateId);
      const pdfData = response.data;
      commit("SET_PDF_DATA", pdfData);

      return pdfData;
    } catch (error) {
      console.error("Failed to load PDF file:", error);
      commit("SET_PDF_ERROR", error.message || "Failed to load PDF");
      throw error;
    } finally {
      commit("SET_PDF_LOADING", false);
    }
  },

};

const getters = {
  /**
   * Get all annotations as an array
   */
  allAnnotations: (state) => {
    return Object.values(state.annotations);
  },

  /**
   * Get annotations for a specific page
   */
  getAnnotationsForPage: (state) => (page) => {
    return Object.values(state.annotations).filter((ann) => ann.page === page);
  },

  /**
   * Get a single annotation by ID
   */
  getAnnotationById: (state) => (id) => {
    return state.annotations[id];
  },

  /**
   * Get annotations by type
   */
  getAnnotationsByType: (state) => (type) => {
    return Object.values(state.annotations).filter((ann) => ann.type === type);
  },

  /**
   * Get selected annotation
   */
  selectedAnnotation: (state) => state.selectedAnnotation,

  /**
   * Get field values
   */
  fieldValues: (state) => state.fieldValues,

  /**
   * Get a specific field value
   */
  getFieldValue: (state) => (fieldName) => {
    return state.fieldValues[fieldName] || null;
  },

  /**
   * Check if adding a field
   */
  isAddingField: (state) => state.addingField,

  /**
   * Get the type of field being added
   */
  addingFieldType: (state) => state.addingFieldType,

  /**
   * Get editing field name
   */
  editingField: (state) => state.editingField,

  /**
   * Validate field name is unique
   */
  validateFieldName: (state) => (fieldName, excludeId) => {
    const existing = Object.values(state.annotations).find(
      (ann) => ann.fieldName === fieldName && ann.id !== excludeId,
    );
    return !existing;
  },

  /**
   * Get total annotation count
   */
  annotationCount: (state) => {
    return Object.keys(state.annotations).length;
  },

  /**
   * PDF related getters
   */
  pdfData: (state) => state.pdfData,
  pdfUrl: (state) => state.pdfUrl,
  templateData: (state) => state.templateData,
  loadingPdf: (state) => state.loadingPdf,
  pdfError: (state) => state.pdfError,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
